import * as React from "react";
import { connect } from "react-redux";
import { Loader } from "semantic-ui-react";
import { uniq, flatten, values } from "lodash";

import { QuestionnaireConfig } from "@teamprevent/common/questionnaires/questionnaireConfig";
import { CustomerSraDepartment } from "@teamprevent/common/customerSraDepartment/model";
import {
    StressRiskAssessmentManagerForm,
    QUESTIONNAIRE_KEY
} from "@teamprevent/common/stressRiskAssessment/components/StressRiskAssessmentManagerForm";
import { AnsweredQuestionWithId } from "@teamprevent/common/questionnaires/model";

import { submitQuestionnaire } from "../actions";
import { ApplicationState } from "../../applicationState";
import { loadSraManagerConfig } from "../../questionnaireConfig/actions";
import { selectAndMergeSraQuestionnaireConfig } from "../selectors";

import { push } from "redux-little-router";

interface StateProps {
    ohCaseId: string;
    questionnaireConfig: QuestionnaireConfig;
    customerSraDepartments?: CustomerSraDepartment[];
    questions: AnsweredQuestionWithId[];
}

interface DispatchProps {
    loadQuestionnaireConfig: () => void;
    submitQuestionnaire: (answers: AnsweredQuestionWithId[], questionnaireKey: string) => Promise<boolean>;
    returnToHomePage: () => void;
}

type Props = StateProps & DispatchProps;

export const WritableStressRiskAssessmentFormUnconnected: React.FunctionComponent<Props> = (props: Props) => {
    const [submittingQuestionnaire, setSubmittingQuestionnaire] = React.useState<boolean>(false);

    React.useEffect(() => {
        props.loadQuestionnaireConfig();
    }, []);

    const loadQuestionnaire = () => {
        // Do nothing
    };

    const submit = async (answers: AnsweredQuestionWithId[], questionnaireKey: string): Promise<boolean> => {
        if (submittingQuestionnaire) {
            return false;
        }

        setSubmittingQuestionnaire(true);

        try {
            const submitted = await props.submitQuestionnaire(answers, questionnaireKey);
            if (answers.every(a => a.isValid)) {
                props.returnToHomePage();
                return submitted;
            } else {
                return false;
            }
        }
        finally {
            setSubmittingQuestionnaire(false);
        }
    };

    return (
        <>
            {props.questionnaireConfig ?
                (<StressRiskAssessmentManagerForm
                    readonly={false}
                    questionnaireConfig={props.questionnaireConfig}
                    submitQuestionnaire={submit}
                    submittingQuestionnaire={submittingQuestionnaire}
                    loadQuestionnaire={loadQuestionnaire}
                    customerSraDepartments={props.customerSraDepartments}
                    questions={props.questions}
                />) :
                (<Loader active />)
            }
        </>);
};

const mapStateToProps = (state: ApplicationState): StateProps => ({
    ohCaseId: state.router.params.ohCaseId,
    questionnaireConfig: selectAndMergeSraQuestionnaireConfig(state),
    customerSraDepartments: state.customerSraDepartments,
    questions: state.questionnaires[QUESTIONNAIRE_KEY] ?
        uniq(flatten(state.questionnaires[QUESTIONNAIRE_KEY].sections.map(section => values(section.questions)))) :
        []
});

const mapDispatchToProps = (dispatch: any): DispatchProps => ({
    loadQuestionnaireConfig: () => dispatch(loadSraManagerConfig()),
    submitQuestionnaire: (answers: AnsweredQuestionWithId[], questionnaireKey: string) =>
        dispatch(submitQuestionnaire(answers, questionnaireKey)),
    returnToHomePage: () => dispatch(push("/"))
});

export const WritableStressRiskAssessmentForm =
    connect(mapStateToProps, mapDispatchToProps)(WritableStressRiskAssessmentFormUnconnected);
